import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { isPresent, isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { addMinutes, isFuture, endOfDay, startOfDay, subDays, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { timeout, dropTask, restartableTask } from 'ember-concurrency';
import type AccessEventModel from 'garaje/models/access-event';
import type { FilterItem } from 'garaje/models/common';
import type { Field } from 'garaje/models/location';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentLocationService from 'garaje/services/current-location';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { modifyDateInTimeZone, parseYyyyMmDdInTimeZone, DATE_FNS_YYYY_MM_DD } from 'garaje/utils/date-fns-tz-utilities';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import { and, notEmpty, reads } from 'macro-decorators';

import type { WorkplaceAccessEventsRouteModel } from './route';

const MIN_SEARCH_LENGTH = 3;

const HEADER_ENUMS = {
  name: 'Name',
  autoSignInStatus: 'Auto sign-in status',
  entranceName: 'Entrance Name',
  integrationName: 'Integration Name',
  eventTimeHeader: 'Event Time',
  receivedAtHeader: 'Received at',
  date: 'Date',
  eventType: 'Event Type',
};

const PRIMARY_HEADERS = [
  {
    name: HEADER_ENUMS.name,
    componentName: 'custom-column',
    sort: 'person-name',
    tooltip: 'Person who triggered the access event',
  },
  { name: HEADER_ENUMS.entranceName, componentName: 'custom-column', tooltip: 'Door or entry point that was accessed' },
  {
    name: HEADER_ENUMS.integrationName,
    componentName: 'custom-column',
    tooltip: 'Integration that reported the event',
    tooltipPosition: 'bottom',
  },
  {
    name: HEADER_ENUMS.eventTimeHeader,
    componentName: 'custom-column',
    sort: 'event-timestamp',
    tooltip: 'When the access event actually occurred',
    tooltipPosition: 'bottom',
  },
  {
    name: HEADER_ENUMS.receivedAtHeader,
    componentName: 'custom-column',
    sort: 'received-at-timestamp',
    tooltip: 'When event was received by our services',
    tooltipPosition: 'top',
  },
  {
    name: HEADER_ENUMS.eventType,
    componentName: 'custom-column',
    tooltip: 'Type of access event outcome',
    tooltipPosition: 'top',
  },
];

const ACTIVITY_TYPES: FilterItem[] = [
  {
    name: 'All events',
    filter: 'ALL',
    show: true,
    tooltipPosition: 'bottom',
    tooltip: 'All access events',
  },
  {
    name: 'Sign-Ins',
    filter: 'SIGN_INS',
    show: true,
    tooltipPosition: 'top',
    tooltip: 'Access events that resulted in a Sign-in',
  },
];

interface SortableField extends Field {
  sort?: string;
  tooltip?: string;
  tooltipPosition?: string;
}

interface PluginStatusResponse {
  jsonapi: {
    version: string;
  };
  data: {
    type: string;
    id: string;
    relationships: {
      'plugin-install': {
        data: {
          id: string;
          type: string;
        };
      };
      location: {
        data: {
          id: string;
          type: string;
        };
      };
    };
    attributes: {
      'last-successful-event'?: string | null;
      'current-status': 'OK' | 'FAILURE' | 'UNKNOWN' | 'MISSING' | 'WARNING';
      'integration-name': string;
    };
  }[];
}

type PluginInstallIdentifier = string;

enum Status {
  OK = 'OK',
  FAILURE = 'FAILURE',
  UNKNOWN = 'UNKNOWN',
  MISSING = 'MISSING',
  WARNING = 'WARNING',
}
interface BasePluginState {
  status: Status;
  lastSuccessfulEvent?: string | null; // ISO string for OffsetDateTime (optional - missing for Indeterminate state)
}

export interface PluginInstallStatus {
  installId: PluginInstallIdentifier;
  locationId: string | null;
  zoneId: string | null;
  currentStatus: BasePluginState;
  isDismissed: boolean;
  integrationName: string;
}

export default class WorkplaceAccessEventsController extends Controller {
  declare model: WorkplaceAccessEventsRouteModel;
  declare limit: number;

  @service declare currentLocation: CurrentLocationService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare ajax: AjaxFetchService;

  queryParams = ['startDate', 'endDate', 'date', 'query', 'filter', 'refresh', 'sort', 'activityType'];
  private calendarRef: HTMLElement | null = null;

  @tracked refresh = ''; // Flag to force a model refresh
  @tracked date: string | null = '';

  @tracked inputQuery: string | null = null;
  @tracked filter = 'all';

  // query params
  @tracked query = '';
  @tracked startDate: Date | null = startOfDay(new Date());
  @tracked endDate: Date | null = endOfDay(new Date());
  @tracked reset: boolean = false;

  @tracked startTime: Date | null = startOfDay(new Date());
  @tracked endTime: Date | null = endOfDay(new Date());

  @tracked totalAccessEvents: number | null = null;
  @tracked showCalendar = false;
  @tracked selectedDateRange = 'Today';
  @tracked pluginStatuses: PluginInstallStatus[] = [];

  @tracked currentActivity = 'ALL';

  @action
  setTotalAccessEvents(total: number): void {
    this.totalAccessEvents = total;
  }

  mapStatus(status: string): Status {
    switch (status) {
      case 'OK':
        return Status.OK;
      case 'FAILURE':
        return Status.FAILURE;
      case 'UNKNOWN':
        return Status.UNKNOWN;
      case 'MISSING':
        return Status.MISSING;
      case 'WARNING':
        return Status.WARNING;
      default:
        return Status.FAILURE; // Default to FAILURE for any unrecognized status
    }
  }

  parsePluginStatuses(response: PluginStatusResponse): PluginInstallStatus[] {
    const dismissedPluginStatuses = this.pluginStatuses.filter((it) => {
      return it.isDismissed;
    });
    return response.data
      .map((resource) => {
        const pluginInstallId = resource.relationships['plugin-install'].data.id;
        let isDismissed = false;
        // early return if required relationships are missing
        if (dismissedPluginStatuses.some((it) => it.installId === pluginInstallId)) {
          isDismissed = true;
        }

        return {
          installId: resource.relationships['plugin-install'].data.id,
          locationId: resource.relationships['location'].data.id,
          zoneId: null,
          currentStatus: {
            status: this.mapStatus(resource.attributes['current-status']),
            lastSuccessfulEvent:
              'last-successful-event' in resource.attributes ? resource.attributes['last-successful-event'] : null,
          },
          isDismissed: isDismissed,
          integrationName: resource.attributes['integration-name'],
        };
      })
      .filter((it) => it !== null) as PluginInstallStatus[];
  }

  @notEmpty('query') showClearButton!: boolean;
  @and('page', 'loadAccessEvents.isIdle', 'mutateQuery.isIdle') showCount!: boolean;
  @reads('currentLocation.timezone', 'America/Los_Angeles') timezone!: string;

  @action
  formatTimestamp(timestamp?: string | null): string {
    if (!timestamp) return 'No event recorded';

    const date = new Date(timestamp);
    return date.getTime() ? format(date, "MMMM do, 'at' h:mma") : 'Invalid timestamp';
  }

  @action
  dismissPluginInstallStatusNotification(pluginInstallId: string): void {
    const pluginStatus = this.pluginStatuses.find((it) => it.installId === pluginInstallId);
    // set the plugin status to dismissed
    if (pluginStatus) {
      pluginStatus.isDismissed = true;
    }
    this.pluginStatuses = [...this.pluginStatuses];
  }

  get minSearchLength(): number {
    return MIN_SEARCH_LENGTH;
  }

  get todayAsString(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get todayAsDate(): Date {
    return parseYyyyMmDdInTimeZone(this.todayAsString, new Date(), this.timezone);
  }

  get isToday(): boolean {
    const { dateWithDefault, startDateWithDefault, endDateWithDefault, todayAsString } = this;

    if (dateWithDefault !== todayAsString) return false;
    if (formatInTimeZone(startDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;
    if (formatInTimeZone(endDateWithDefault, this.timezone, DATE_FNS_YYYY_MM_DD) !== todayAsString) return false;

    return true;
  }

  get isAfterToday(): boolean {
    const { startDateWithDefault, endDateWithDefault } = this;

    return isFuture(startDateWithDefault) && isFuture(endDateWithDefault);
  }

  get currentQuery(): string {
    // If a search query typed in, use it.
    // Fallback to query param.
    const { inputQuery, query } = this;

    return isNone(inputQuery) ? query : inputQuery;
  }

  get dateRangeFilterOptions(): string[] {
    const { selectedDateRangeWithDefault } = this;
    const options = ['Today', 'Yesterday', 'Past 7 days', 'Past 14 days', 'Past 30 days', 'Custom range'];

    if (!options.includes(selectedDateRangeWithDefault)) {
      options.push(selectedDateRangeWithDefault);
    }

    return options;
  }

  get dateWithDefault(): string {
    return this.date || formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.startDate) return new Date(this.startDate);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endDateWithDefault(): Date {
    if (this.endDate) return new Date(this.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  get startTimeWithDefault(): Date {
    if (this.startTime) return new Date(this.startTime);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);

    return start;
  }

  get endTimeWithDefault(): Date {
    if (this.endTime) return new Date(this.endTime);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  get selectedDateRangeWithDefault(): string {
    const { selectedDateRange, isToday } = this;

    if (isToday) return 'Today';
    if (selectedDateRange) return selectedDateRange;

    return 'Custom range';
  }

  @action
  didSelectDateRange(startDate: Date, endDate: Date): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_DATE_RANGE_SELECT_CLICKED');
    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedDateRange = 'Custom range';
    this.date = '';
  }

  get startDateFormat(): string {
    const { timezone, startDateWithDefault } = this;

    return formatInTimeZone(startDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get endDateFormat(): string {
    const { timezone, endDateWithDefault } = this;

    return formatInTimeZone(endDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  @action
  setStartTime(date: Date): void {
    this.startTime = date;
  }

  @action
  setEndTime(date: Date): void {
    this.endTime = date;
  }

  @action
  setStartDate(date: Date): void {
    this.startDate = date;
    this.reset = true;
  }

  @action
  setEndDate(date: Date): void {
    this.endDate = date;
    this.reset = true;
  }

  @action
  didSelectTimeRange(startTime: Date, endTime: Date): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_TIME_RANGE_SELECT_CLICKED');
    this.startTime = startTime;
    this.endTime = endTime;
    this.selectedDateRange = 'Custom range';
    this.date = '';
  }

  @action
  setStartAndEndTime(): void {
    this.startTime = this.startTimeWithDefault;
    this.endTime = this.endTimeWithDefault;
    this.startDate = this.startDateWithDefault;
    this.endDate = this.endDateWithDefault;
  }

  @action
  initializeReset(): void {
    this.reset = false;
  }

  get allAccessEvents(): AccessEventModel[] {
    return this.model.accessEvents;
  }

  get relevantAccessEvents(): AccessEventModel[] {
    // eslint-disable-next-line ember/no-get
    return get(this.model, 'accessEvents');
  }

  get primaryHeaders(): SortableField[] {
    return PRIMARY_HEADERS;
  }

  get fieldOptions(): Field[] {
    const alwaysEnabledColumns = ['Name', 'Event Time', 'Event Type'];
    const primaryHeaders = this.primaryHeaders;
    let headers: SortableField[] = [...primaryHeaders];
    headers = headers.map(({ name, componentName, sort, tooltip, tooltipPosition }) => {
      return {
        name,
        componentName,
        // always show alwaysEnabledColumns
        show:
          alwaysEnabledColumns.includes(name) ||
          isPresent(this.currentLocation.location.dashboardFields.find((field) => field.name === name)),
        // do not allow disabling of alwaysEnabledColumns
        disabled: alwaysEnabledColumns.includes(name),
        sort,
        tooltip,
        tooltipPosition,
      };
    });
    return headers;
  }

  @action
  updateInputQuery(employeeName: string): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_SEARCHED_EMPLOYEES');

    // Set input value immediately.
    // This reduces risk of rendered input reverting to a previous value.
    this.inputQuery = employeeName;
    if (employeeName) {
      void this.mutateQuery.perform(this.inputQuery);
    } else {
      this.clearSearch();
    }
  }

  mutateQuery = restartableTask(async (employeeName: string) => {
    this.reset = false;
    if (this.query === employeeName) return;

    // Stop if the query is "too short"
    if (employeeName.length < this.minSearchLength) return;

    await timeout(zft(500));

    this.query = employeeName;

    // resetting this to 0 will trigger a new fetch
    this.reset = true;
    this.totalAccessEvents = null;

    this.metrics.trackEvent('Access Events Searched', { search_value: this.query });
  });

  get undismissedPluginStatuses(): PluginInstallStatus[] {
    return this.pluginStatuses.filter((it) => {
      return (
        !it.isDismissed &&
        (it.currentStatus.status === Status.FAILURE ||
          it.currentStatus.status === Status.WARNING ||
          it.currentStatus.status === Status.UNKNOWN ||
          it.currentStatus.status === Status.MISSING)
      );
    });
  }

  loadPluginStatuses = dropTask(async () => {
    const url = urlBuilder.pluginStatusesUrl();
    const settings = {
      accept: 'application/vnd.api+json',
      data: { filter: { 'location-ids': this.currentLocation.location.id } },
    };
    const response = await this.ajax.request<PluginStatusResponse>(url, settings);
    this.pluginStatuses = this.parsePluginStatuses(response);

    // fetch plugin-installs if not in store
    const installIds = this.pluginStatuses.map((status) => status.installId);
    const pluginInstalls = this.store.peekAll('plugin-install');
    const shouldFetch = installIds.some((installId) => !pluginInstalls.findBy('id', installId));
    if (shouldFetch) {
      await this.store.query('plugin-install', { filter: { location: this.currentLocation.location.id } });
    }
  });

  @action
  onDateRangeSelect(option: string): void {
    this.workplaceMetrics.trackEvent('ACCESS_EVENT_LOG_DATE_RANGE_SELECT_CLICKED');

    let start = startOfDay(new Date());
    let end = endOfDay(new Date());

    switch (option) {
      case 'Yesterday':
        start = subDays(start, 1);
        end = subDays(end, 1);
        break;
      case 'Past 7 days':
        start = subDays(start, 7);
        break;
      case 'Past 14 days':
        start = subDays(start, 14);
        break;
      case 'Past 30 days':
        start = subDays(start, 30);
        break;
      case 'Custom range':
        return;
    }

    this.date = '';
    this.selectedDateRange = option;
    this.startDate = addMinutes(start, this.state.minutesBetweenTimezones(start));
    this.endDate = addMinutes(end, this.state.minutesBetweenTimezones(end));
    this.reset = true;
  }

  @action
  toggleDashboardField({ name, componentName, show }: Field & { show: boolean }): void {
    let dashboardFields = this.currentLocation.location.dashboardFields;

    if (show) {
      dashboardFields.push({ name, componentName });
    } else {
      dashboardFields = dashboardFields.filter((field) => field.name !== name);
    }

    this.currentLocation.location.dashboardFields = dashboardFields;
    void this.currentLocation.location.save();
  }

  @action
  clearSearch(): void {
    this.reset = true;
    this.inputQuery = null;
    this.query = '';
  }

  @action
  setCalendarRef(element: HTMLElement): void {
    this.calendarRef = element;
  }

  @action
  handleBlur(event: FocusEvent): void {
    if (this.calendarRef && !this.calendarRef.contains(event.relatedTarget as Node)) {
      this.showCalendar = false;
    }
  }

  @action
  toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  @action
  selectActivity(activity: FilterItem): void {
    this.currentActivity = activity.filter;
    this.reset = true;
  }

  get activityTypes(): FilterItem[] {
    return ACTIVITY_TYPES;
  }

  get selectedActivityType(): FilterItem | undefined {
    const filter = this.currentActivity;
    const filterOptions = this.activityTypes;
    const selectedOption = filterOptions.find((item) => item.filter == filter);
    return selectedOption || filterOptions[0];
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'workplace.access-events': WorkplaceAccessEventsController;
  }
}
